/**
 * Mesaj Formatleyici
 * Kullanım: formatter.platform("whatsapp").format(message)
 */

// Platform formatları tanımları
const PLATFORM_FORMATS = {
    whatsapp: {
        bold: (text) => `*${text}*`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        underline: null,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
    instagram: {
        bold: (text) => `*${text}*`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        underline: null,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
    messenger: {
        bold: (text) => `"${text}"`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        underline: null,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
};

class MessageFormatter {
    constructor() {
        this.selectedPlatform = null;
    }

    platform(platformName) {
        this.selectedPlatform = platformName;
        return this;
    }

    format(message, isPrivateMessage = false) {
        if (!message) return '';
        if (!this.selectedPlatform) return message;
        
        // Private message'larda formatlama yapma
        if (isPrivateMessage) return message;

        const formatters = {
            whatsapp: () => this.applyTextFormat(message, PLATFORM_FORMATS.whatsapp),
            web: () => message,
            instagram: () => this.applyTextFormat(message, PLATFORM_FORMATS.instagram),
            telegram: () => this.formatTelegram(message),
            messenger: () => this.applyTextFormat(message, PLATFORM_FORMATS.messenger),
            trendyol: () => this.htmlToText(message),
            linkedin: () => this.htmlToText(message),
        };

        const formatter = formatters[this.selectedPlatform];
        return formatter ? formatter() : message;
    }

    // Platform formatından HTML'e dönüştür (Reverse)
    parse(message) {
        if (!message) return '';
        if (!this.selectedPlatform) return message;

        const parsers = {
            whatsapp: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
            web: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp), // Web'de de platform formatları destekle
            instagram: () => this.parseTextFormat(message, PLATFORM_FORMATS.instagram),
            telegram: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp), // Telegram'da da destekle
            messenger: () => this.parseTextFormat(message, PLATFORM_FORMATS.messenger),
            trendyol: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp), // Trendyol'da da destekle
            linkedin: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp), // LinkedIn'de de destekle
        };

        const parser = parsers[this.selectedPlatform];
        return parser ? parser() : message;
    }

    // Ortak format uygulama fonksiyonu
    applyTextFormat(message, formatRules) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(message, 'text/html');

        // İç içe formatları işle
        const result = this.processElement(doc.body, formatRules);

        return result.trim();
    }

    // Element'i işle ve formatlanmış string döndür
    processElement(element, formatRules) {
        let result = '';

        element.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                result += node.textContent;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.tagName.toLowerCase();
                const style = node.getAttribute('style') || '';
                
                // Link tag'i içindeki metni hiç formatlama, düz text al
                if (tagName === 'a') {
                    const href = node.getAttribute('href');
                    const plainText = node.textContent || node.innerText || '';
                    result += href && href !== plainText ? `${plainText} (${href})` : plainText;
                    return; // Döngünün bu iterasyonunu bitir
                }
                
                const innerText = this.processElement(node, formatRules);

                // Tag veya style'a göre format uygula
                let formatted = false;

                // Layout tag'leri: br, p, div -> newline
                if (tagName === 'br') {
                    result += '\n';
                    formatted = true;
                }
                else if (tagName === 'p' || tagName === 'div') {
                    result += innerText + '\n';
                    formatted = true;
                }
                // Bold: <b>, <strong> veya style="font-weight: bold"
                else if (((tagName === 'b' || tagName === 'strong') || this.hasStyle(style, 'font-weight', ['bold', '700', '800', '900'])) && formatRules.bold) {
                    result += formatRules.bold(innerText);
                    formatted = true;
                }
                // Italic: <i>, <em> veya style="font-style: italic"
                else if (((tagName === 'i' || tagName === 'em') || this.hasStyle(style, 'font-style', ['italic', 'oblique'])) && formatRules.italic) {
                    result += formatRules.italic(innerText);
                    formatted = true;
                }
                // Strikethrough: <s>, <strike>, <del> veya style="text-decoration: line-through"
                else if (((tagName === 's' || tagName === 'strike' || tagName === 'del') || this.hasStyle(style, 'text-decoration', ['line-through'])) && formatRules.strikethrough) {
                    result += formatRules.strikethrough(innerText);
                    formatted = true;
                }
                // Underline: <u> veya style="text-decoration: underline"
                else if ((tagName === 'u' || this.hasStyle(style, 'text-decoration', ['underline'])) && formatRules.underline) {
                    result += formatRules.underline(innerText);
                    formatted = true;
                }
                // Monospace: <pre> veya <pre><code>
                else if (tagName === 'pre' && formatRules.monospace) {
                    result += formatRules.monospace(innerText);
                    formatted = true;
                }
                // Code: <code> (pre içinde değilse)
                else if (tagName === 'code' && formatRules.code && element.tagName.toLowerCase() !== 'pre') {
                    result += formatRules.code(innerText);
                    formatted = true;
                }
                // Blockquote: <blockquote>
                else if (tagName === 'blockquote' && formatRules.blockquote) {
                    result += formatRules.blockquote(innerText);
                    formatted = true;
                }

                if (!formatted) {
                    result += innerText;
                }
            }
        });

        return result;
    }

    // Style attribute'unda belirli bir CSS özelliğinin varlığını kontrol et
    hasStyle(styleAttr, property, values) {
        if (!styleAttr) return false;
        const styles = styleAttr.toLowerCase().split(';');
        for (const style of styles) {
            const [prop, value] = style.split(':').map(s => s.trim());
            if (prop === property) {
                return values.some(v => value.includes(v));
            }
        }
        return false;
    }



    // Telegram: HTML subset (<b>, <i>, <u>, <s>, <code>)
    formatTelegram(message) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(message, 'text/html');

        // Desteklenmeyen tagleri temizle
        const allowedTags = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'code', 'pre', 'a'];
        doc.querySelectorAll('*').forEach((el) => {
            if (!allowedTags.includes(el.tagName.toLowerCase())) {
                const text = el.innerHTML;
                el.outerHTML = text;
            }
        });

        return doc.body.innerHTML;
    }

    // HTML'i düz texte çevir
    htmlToText(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return (doc.body.textContent || doc.body.innerText || '').trim();
    }

    // Platform formatını HTML'e çevir (Reverse)
    parseTextFormat(text, formatRules) {
        // HTML tag'lerini formatlama regex'lerinden koru
        var htmlTags = [];
        text = text.replace(/<[^>]*>/g, function(tag) {
            var index = htmlTags.length;
            htmlTags.push(tag);
            return '\x00HTAG' + index + '\x00';
        });

        // String'i URL ve non-URL parçalara ayır
        const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        // URL'leri bul ve string'i parçalara ayır
        while ((match = urlRegex.exec(text)) !== null) {
            // URL'den önceki kısım (non-URL)
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: text.substring(lastIndex, match.index)
                });
            }
            // URL kısmı
            parts.push({
                type: 'url',
                content: match[0]
            });
            lastIndex = match.index + match[0].length;
        }

        // Kalan kısım (non-URL)
        if (lastIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex)
            });
        }

        // Eğer hiç URL yoksa tüm text'i ekle
        if (parts.length === 0) {
            parts.push({
                type: 'text',
                content: text
            });
        }

        // Sadece non-URL parçalarda formatlama yap
        const formattedParts = parts.map(part => {
            if (part.type === 'url') {
                return part.content; // URL'leri olduğu gibi bırak
            }

            let result = part.content;

            // Blockquote: > text -> <blockquote>text</blockquote>
            if (formatRules.blockquote) {
                result = result.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');
            }

            // Monospace: ```text``` -> <pre>text</pre>
            if (formatRules.monospace) {
                result = result.replace(/```(.+?)```/gs, '<pre>$1</pre>');
            }

            // Code: `text` -> <code>text</code>
            if (formatRules.code) {
                result = result.replace(/`(.+?)`/g, '<code>$1</code>');
            }

            // İç içe formatlar için recursive parse
            let hasChange = true;
            let iterations = 0;
            const maxIterations = 10;

            while (hasChange && iterations < maxIterations) {
                const before = result;
                iterations++;

                // Strikethrough: ~text~ -> <s>text</s>
                if (formatRules.strikethrough) {
                    result = result.replace(/(?<![a-zA-Z0-9])~(.+?)~(?![a-zA-Z0-9])/g, '<s>$1</s>');
                }

                // Italic: _text_ -> <i>text</i>
                if (formatRules.italic) {
                    result = result.replace(/(?<![a-zA-Z0-9])_(.+?)_(?![a-zA-Z0-9])/g, '<i>$1</i>');
                }

                // Bold: *text* veya "text" -> <b>text</b>
                if (formatRules.bold) {
                    if (formatRules.bold('x') === '*x*') {
                        result = result.replace(/(?<![a-zA-Z0-9])\*(.+?)\*(?![a-zA-Z0-9])/g, '<b>$1</b>');
                    } else if (formatRules.bold('x') === '"x"') {
                        result = result.replace(/(?<![a-zA-Z0-9])"(.+?)"(?![a-zA-Z0-9])/g, '<b>$1</b>');
                    }
                }

                hasChange = before !== result;
            }

            return result;
        });

        // Tüm parçaları birleştir
        var finalResult = formattedParts.join('');

        // HTML tag placeholder'larını geri koy
        htmlTags.forEach(function(tag, i) {
            finalResult = finalResult.replace('\x00HTAG' + i + '\x00', tag);
        });

        return finalResult;
    }

    // Tüm formatları çıkar (hem HTML hem platform formatları)
    stripAllFormats(message, platform = 'whatsapp') {
        if (!message) return '';
        
        // Önce platform formatlarını parse et (HTML'e çevir)
        let result = this.platform(platform).parse(message);
        
        // Sonra tüm HTML tag'lerini çıkar
        const parser = new DOMParser();
        const doc = parser.parseFromString(result, 'text/html');
        return (doc.body.textContent || doc.body.innerText || '').trim();
    }
}

// Singleton instance
const formatter = new MessageFormatter();

// Standalone fonksiyon export et
export function stripAllFormats(message, platform = 'whatsapp') {
    return formatter.stripAllFormats(message, platform);
}

export default formatter;
